import React from "react";
import { Button, DialogContentText, MenuItem, Select } from "@mui/material";
import { IUser } from "../../../../api/homesites/homesites.types";
import { HomeSitesDataIndex } from "../../../../reactQuery/homeSitesDataIndex/types";
import { thinDarkScrollbar } from "../../../../utils/scrollbarStyles";
import { BaseDialog } from "./BaseDialog";

interface ReassignDialogProps {
     open: boolean;
     selectedBulkConcierge: IUser | null;
     communityToReassign: string;
     targetConcierge: IUser | null;
     homesites: HomeSitesDataIndex[];
     sortedConcierges: IUser[];
     userInfo: any;
     isBulkAssigning: boolean;
     onConfirm: () => void;
     onCancel: () => void;
     onTargetConciergeChange: (concierge: IUser | null) => void;
}

export const ReassignDialog: React.FC<ReassignDialogProps> = ({
     open,
     selectedBulkConcierge,
     communityToReassign,
     targetConcierge,
     homesites,
     sortedConcierges,
     userInfo,
     isBulkAssigning,
     onConfirm,
     onCancel,
     onTargetConciergeChange,
}) => {
     const homesitesToUpdate = homesites?.filter((hs) => hs.communityName === communityToReassign) || [];
     const homesitesNeedingUpdate = homesitesToUpdate.filter(
          (hs) =>
               hs.conciergeSource !== "SF Opportunity" &&
               hs.conciergeName === selectedBulkConcierge?.name &&
               hs.conciergeEmail === selectedBulkConcierge?.email
     );

     const actions = (
          <>
               <Button
                    onClick={onCancel}
                    color="primary"
                    variant="outlined"
                    disabled={isBulkAssigning}
                    style={{ textTransform: "none", border: "2px solid #005DAA" }}
               >
                    Cancel
               </Button>
               <Button
                    onClick={onConfirm}
                    color="primary"
                    variant="contained"
                    disabled={!targetConcierge || isBulkAssigning}
                    style={{ textTransform: "none" }}
               >
                    Reassign
               </Button>
          </>
     );

     return (
          <BaseDialog
               open={open}
               title="Reassign Concierge to Another Concierge"
               maxWidth="xs"
               fullWidth
               showDivider
               disabled={isBulkAssigning}
               onClose={onCancel}
               actions={actions}
          >
               <DialogContentText style={{ marginBottom: "16px" }}>
                    Reassign homesites from <strong>{selectedBulkConcierge?.name}</strong> in <strong>{communityToReassign}</strong> to:
                    {homesitesNeedingUpdate.length > 0 && (
                         <span style={{ display: "block", marginTop: "8px", fontSize: "13px", color: "#666" }}>
                              This will reassign {homesitesNeedingUpdate.length} homesite{homesitesNeedingUpdate.length === 1 ? "" : "s"}.
                         </span>
                    )}
               </DialogContentText>

               <div>
                    <DialogContentText style={{ marginBottom: "8px", fontSize: "14px", fontWeight: "500" }}>
                         Select Target Concierge
                    </DialogContentText>
                    <Select
                         value={targetConcierge?.name || ""}
                         onChange={(e) => {
                              const concierge = sortedConcierges.find((c) => c.name === e.target.value);
                              onTargetConciergeChange(concierge || null);
                         }}
                         displayEmpty
                         fullWidth
                         disabled={isBulkAssigning}
                         renderValue={(selected) => {
                              if (!selected) {
                                   return <span style={{ color: "#5B7282", fontWeight: "lighter" }}>Select a concierge</span>;
                              }
                              return selected;
                         }}
                         MenuProps={{
                              PaperProps: {
                                   sx: {
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        ...thinDarkScrollbar,
                                   },
                              },
                         }}
                    >
                         {sortedConcierges
                              .filter((c) => c.email !== selectedBulkConcierge?.email) // Exclude current concierge
                              .map((c) => (
                                   <MenuItem key={c.email} value={c.name}>
                                        {c.name} {c.email === userInfo?.email && "(You)"}
                                   </MenuItem>
                              ))}
                    </Select>
               </div>
          </BaseDialog>
     );
};
